import { api } from '@/lib/api'
import type {
  AuditLogResponse,
  ContactMessageDetailResponse,
  ContactMessageRow,
  ContactMessageReply,
  ContactMessageCategory,
  ContactMessageStatus,
  ContactMessagesResponse,
  CreateStaffInvitationResponse,
  CreateTenantPayload,
  CustomerUserSearchResponse,
  DbRowsResponse,
  ImpersonateResponse,
  OverviewResponse,
  PlatformTenant,
  StaffInvitation,
  StaffMember,
  StaffPermission,
  StaffRole,
  UpdateContactMessagePayload,
} from './types'

export const platformApi = {
  overview: () => api.get<OverviewResponse>('/api/admin/overview'),

  // Tenants
  listTenants: (includeDeleted = false) =>
    api.get<{ tenants: PlatformTenant[] }>('/api/admin/tenants', {
      query: { include_deleted: includeDeleted ? 'true' : '' },
    }),
  createTenant: (body: CreateTenantPayload) =>
    api.post<{ tenant: PlatformTenant }>('/api/admin/tenants', body),
  changePlan: (id: string, plan: string) =>
    api.post<{ tenantId: string; plan: string }>(`/api/admin/tenants/${id}/plan`, { plan }),
  suspendTenant: (id: string) =>
    api.post<{ tenant: PlatformTenant }>(`/api/admin/tenants/${id}/suspend`),
  activateTenant: (id: string) =>
    api.post<{ tenant: PlatformTenant }>(`/api/admin/tenants/${id}/activate`),
  deleteTenant: (id: string) =>
    api.del<{ tenant: PlatformTenant }>(`/api/admin/tenants/${id}`),
  impersonate: (tenantId: string) =>
    api.post<ImpersonateResponse>('/api/admin/impersonate', { tenantId }),

  // Customer users
  listCustomerUsers: (params: { search?: string; limit?: number; offset?: number } = {}) =>
    api.get<CustomerUserSearchResponse>('/api/admin/customer-users', {
      query: {
        search: params.search ?? '',
        limit: params.limit ?? 100,
        offset: params.offset ?? 0,
      },
    }),
  deactivateUser: (id: number) =>
    api.post<{ user: { id: number; email: string; is_active: boolean } }>(
      `/api/admin/customer-users/${id}/deactivate`,
    ),
  activateUser: (id: number) =>
    api.post<{ user: { id: number; email: string; is_active: boolean } }>(
      `/api/admin/customer-users/${id}/activate`,
    ),
  deleteUser: (id: number) =>
    api.del<{ user: { id: number; email: string } }>(
      `/api/admin/customer-users/${id}`,
    ),

  // Staff
  listStaffMembers: () => api.get<{ members: StaffMember[] }>('/api/admin/staff/members'),
  listStaffRoles: () => api.get<{ roles: StaffRole[] }>('/api/admin/staff/roles'),
  listStaffPermissions: () =>
    api.get<{ permissions: StaffPermission[] }>('/api/admin/staff/permissions'),
  setStaffRolePermissions: (roleId: number, permissions: string[]) =>
    api.put<{ count: number }>(`/api/admin/staff/roles/${roleId}/permissions`, { permissions }),
  changeStaffRole: (userId: number, roleId: number) =>
    api.post<{ roleName?: string }>(`/api/admin/staff/members/${userId}/role`, { roleId }),
  deactivateStaffMember: (userId: number) =>
    api.post<unknown>(`/api/admin/staff/members/${userId}/deactivate`),
  activateStaffMember: (userId: number) =>
    api.post<unknown>(`/api/admin/staff/members/${userId}/activate`),
  deleteStaffMember: (userId: number) =>
    api.del<{ id: number; email: string | null }>(`/api/admin/staff/members/${userId}`),

  // Staff invitations
  listStaffInvitations: () =>
    api.get<{ invitations: StaffInvitation[] }>('/api/admin/staff/invitations'),
  createStaffInvitation: (email: string, roleId: number) =>
    api.post<CreateStaffInvitationResponse>('/api/admin/staff/invitations', { email, roleId }),
  revokeStaffInvitation: (id: string) =>
    api.del<{ email: string }>(`/api/admin/staff/invitations/${id}`),

  // Audit
  listAuditLogs: (params: {
    action?: string
    resource_type?: string
    tenant_id?: string
    limit?: number
    offset?: number
  } = {}) =>
    api.get<AuditLogResponse>('/api/admin/audit-logs', {
      query: {
        action: params.action ?? '',
        resource_type: params.resource_type ?? '',
        tenant_id: params.tenant_id ?? '',
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    }),
  deleteAuditLogs: (ids: Array<number | string>) =>
    api.del<{ deleted: number }>('/api/admin/audit-logs', { body: { ids } }),

  // Contact messages
  listContactMessages: (params: {
    status?: ContactMessageStatus | ''
    category?: ContactMessageCategory | ''
    search?: string
    limit?: number
    offset?: number
  } = {}) =>
    api.get<ContactMessagesResponse>('/api/admin/contact-messages', {
      query: {
        status: params.status ?? '',
        category: params.category ?? '',
        search: params.search ?? '',
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    }),
  getContactMessage: (id: number) =>
    api.get<ContactMessageDetailResponse>(`/api/admin/contact-messages/${id}`),
  updateContactMessage: (id: number, body: UpdateContactMessagePayload) =>
    api.patch<{ message: ContactMessageRow }>(`/api/admin/contact-messages/${id}`, body),
  replyContactMessage: (id: number, body: string) =>
    api.post<{ reply: ContactMessageReply }>(
      `/api/admin/contact-messages/${id}/replies`,
      { body },
    ),
  deleteContactMessage: (id: number) =>
    api.del<{ id: number }>(`/api/admin/contact-messages/${id}`),

  // DB tools
  listTables: () => api.get<{ tables: string[] }>('/api/admin/db-tools/tables'),
  fetchTable: (name: string, limit = 100) =>
    api.get<DbRowsResponse>(`/api/admin/db-tools/tables/${encodeURIComponent(name)}`, {
      query: { limit },
    }),
  deleteAllRows: (name: string) =>
    api.del<{ deleted: number }>(`/api/admin/db-tools/tables/${encodeURIComponent(name)}`),
}
